import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarDays, Users, Clock, Loader2, Images } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/motion";
import { getStage } from "@/lib/api";
import { formatDateRange, formatPrice, categoryLabel, stageBasePath, stageTypeLabel } from "@/lib/format";

const StageDetail = () => {
  const { slug } = useParams();
  const [stage, setStage] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    getStage(slug).then((s) => {
      setStage(s);
      setState(s ? "ready" : "notfound");
    });
  }, [slug]);

  if (state === "loading") {
    return (
      <div data-testid="stage-detail-loading" className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-gold" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div data-testid="stage-detail-notfound" className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display uppercase text-5xl text-white">Stage introuvable</h1>
        <p className="mt-4 text-white/60">Ce stage n'existe pas ou n'est plus disponible.</p>
        <Link to="/stage-payet" data-testid="stage-notfound-back" className="mt-8 px-8 py-4 border-2 border-gold text-gold text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all">
          Voir les stages
        </Link>
      </div>
    );
  }

  const status = stage.effective_status || stage.status;
  const isPast = stage.end_date && new Date(stage.end_date) < new Date();
  const isOpen = status === "open" && !isPast;
  const nameParts = stage.name.split("—").map((p) => p.trim());
  const titleLines = [
    <span key="a" className="text-white">{nameParts[0]}</span>,
    ...(nameParts[1] ? [<span key="b" className="text-gold">{nameParts.slice(1).join(" — ")}</span>] : []),
  ];

  return (
    <div data-testid="stage-detail-page">
      <PageHero
        testId="stage-detail-hero"
        image={stage.image || undefined}
        overline={stageTypeLabel(stage)}
        titleLines={titleLines}
        subtitle={stage.description}
      />

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <Link
              to={stageBasePath(stage)}
              data-testid="stage-detail-back"
              className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-gold transition-colors"
            >
              <ArrowLeft size={15} /> Retour aux stages
            </Link>
            <Overline className="mt-10">Le programme</Overline>
            <h2 className="mt-4 font-display uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight">
              Une expérience <span className="text-gold">haut niveau</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed text-base md:text-lg">{stage.description}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {stage.categories.map((cat) => (
                <span key={cat} data-testid={`detail-category-${cat}`} className="text-xs font-bold tracking-wider border border-gold/40 text-gold px-3 py-1.5">
                  {cat}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div data-testid="stage-detail-info" className="bg-noir-surface border border-noir-border p-8 md:p-10 sticky top-28">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <CalendarDays size={18} className="text-gold shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Dates</p>
                    <p className="font-display text-xl tracking-wider text-white">{formatDateRange(stage.start_date, stage.end_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Clock size={18} className="text-gold shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Durée</p>
                    <p className="text-white">{stage.duration_days} jour{stage.duration_days > 1 ? "s" : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <MapPin size={18} className="text-gold shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Lieu</p>
                    <p className="text-white">{stage.location}{stage.location_details ? ` — ${stage.location_details}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Users size={18} className="text-gold shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Catégories</p>
                    <p className="text-white">{categoryLabel(stage.categories)}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tarif</p>
                    <p data-testid="stage-detail-price" className="font-display text-5xl text-gold leading-none mt-1">{formatPrice(stage.price)}</p>
                  </div>
                  {isOpen && (
                    <p data-testid="stage-detail-places" className="text-[11px] font-bold uppercase tracking-[0.15em] bg-royal-light text-white px-3 py-1.5">
                      {stage.places_restantes} place{stage.places_restantes > 1 ? "s" : ""} restante{stage.places_restantes > 1 ? "s" : ""}
                    </p>
                  )}
                </div>

                {isOpen ? (
                  <Link
                    to={`/inscription/${stage.slug}`}
                    data-testid="stage-detail-cta"
                    className="mt-8 block text-center px-8 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_24px_rgba(230,0,0,0.35)]"
                  >
                    S'inscrire
                  </Link>
                ) : (
                  <span
                    data-testid="stage-detail-cta"
                    className="mt-8 block text-center px-8 py-4 bg-white/10 text-white/40 text-sm font-bold uppercase tracking-[0.15em] rounded-full cursor-not-allowed"
                  >
                    {isPast ? "Stage terminé" : status === "full" ? "Complet" : "Inscriptions fermées"}
                  </span>
                )}

                {stage.photos_url && (
                  <a
                    href={stage.photos_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="stage-photos-link"
                    className="mt-4 flex items-center justify-center gap-2 px-8 py-3.5 border-2 border-gold text-gold text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all"
                  >
                    <Images size={15} /> Photos du stage
                  </a>
                )}

                <p className="mt-4 text-white/35 text-xs leading-relaxed">
                  {isPast
                    ? "Ce stage est terminé. Découvrez nos prochaines sessions."
                    : "Paiement sécurisé via Stripe. La place n'est définitivement confirmée qu'après validation du paiement."}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default StageDetail;
